/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { AppWindowIcon } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useId } from "react"

import { ApplicationTypeViewModel } from "../../features/application-type/application-type-viewmodel"
import { useViewModel } from "../app/app-hooks"
import { Button } from "../primitives/button"
import { Card } from "../primitives/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../primitives/empty"
import { Table, TableBody } from "../primitives/table"
import { ApplicationTypeRow } from "./application-type-row"
import { CreateApplicationType, CreateApplicationTypeTrigger } from "./create-application-type"

/**
 * Lists all applications and links each row to its detail page.
 *
 * The header button opens the Create Application dialog. When no applications
 * exist, an empty state replaces the table.
 *
 * The route loads the list and `withLoading` handles the loading spinner, so
 * this view doesn't need its own loading state.
 */
export const AllApplicationTypesView = observer(() => {
    const headingId = useId()
    const vm = useViewModel(ApplicationTypeViewModel)

    return (
        <div className="w-content">
            <div className="max-w-175">
                <div className="mb-6 flex items-center justify-between">
                    <h1 className="m-0 text-3xl font-bold text-primary" id={headingId}>
                        All Applications
                    </h1>
                    <CreateApplicationType />
                </div>

                {vm.hasAllAppTypes ? (
                    <Card className="py-0">
                        {/*
                         * The table has no header row, so use the page heading
                         * as its accessible name.
                         */}
                        <Table aria-labelledby={headingId}>
                            <TableBody>
                                {vm.allAppTypes.map((appType) => (
                                    <ApplicationTypeRow key={appType.id} appType={appType} />
                                ))}
                            </TableBody>
                        </Table>
                    </Card>
                ) : (
                    renderEmptyAppTypes()
                )}
            </div>
        </div>
    )
})

/**
 * Renders the empty state when no applications are available.
 *
 * Unlike other list views, this one uses an icon and a create link rather than
 * an empty table row. The link opens the same dialog as the header button;
 * `CreateApplicationType` handles focus when the dialog closes.
 */
function renderEmptyAppTypes() {
    return (
        <Empty className="border bg-background">
            <EmptyHeader>
                <EmptyMedia>
                    <AppWindowIcon />
                </EmptyMedia>
                <EmptyTitle>No applications found</EmptyTitle>
                <EmptyDescription>
                    You can start by{" "}
                    <CreateApplicationTypeTrigger render={<Button variant="link" className="h-auto p-0 underline" />}>
                        creating a new one
                    </CreateApplicationTypeTrigger>
                    .
                </EmptyDescription>
            </EmptyHeader>
        </Empty>
    )
}
